import { AppLayout } from '@/components/app-layout';
import { AuthCheck } from '@/components/auth-check.component';
import { Head } from '@/components/head.component';
import { finishConnectPopup } from '@/containers/integrations/connect-popup.util';
import {
  ATTIO_ERROR_LABELS,
  humanizeOAuthError,
} from '@/containers/integrations/connect-error-copy.util';
import { focusIntegrationCard } from '@/containers/integrations/focus-integration-card';
import {
  IntegrationsGate,
  type IntegrationsTabParam,
} from '@/containers/integrations/integrations-gate.component';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { toast } from 'sonner';

/**
 * OAuth-outcome params the backend redirects back with (HubSpot / Salesforce /
 * Pipedrive / Attio / Lemlist and the workspace connectors). They were previously discarded entirely
 * (grep found zero readers), so a failed connect was silent — the exact failure
 * mode the I5 tripwire could cause. Validate them so the route can toast + clear.
 */
interface IntegrationsSearch {
  hubspot_connected?: string;
  hubspot_error?: string;
  sf_connected?: string;
  sf_error?: string;
  pipedrive_connected?: string;
  pipedrive_error?: string;
  attio_connected?: string;
  attio_error?: string;
  lemlist_error?: string;
  // Outreach's sign in comes back with its result: connected, denied,
  // expired or error (cleanse-api `_back`).
  outreach?: string;
  google_connected?: string;
  google_error?: string;
  fireflies_connected?: string;
  fireflies_error?: string;
  fathom_connected?: string;
  fathom_error?: string;
  gong_connected?: string;
  gong_error?: string;
  notion_connected?: string;
  notion_error?: string;
  airtable_connected?: string;
  airtable_error?: string;
  sixsense_mcp_connected?: string;
  sixsense_mcp_error?: string;
  // Pre-existing params other screens navigate here with — keep them in the
  // validated shape or those navigations fail to typecheck (the route had no
  // validateSearch before, so it accepted anything). `importCrm` auto-opens a
  // CRM import; `tab` selects a tab (e.g. extension).
  importCrm?: string;
  tab?: IntegrationsTabParam;
  // `connect=email` opens the email provider picker on the Sender
  // connections tab (deep link from the sequence builder).
  connect?: 'email';
  // Result of the hosted email connect flow (see routes/settings/email-accounts).
  connected?: '1';
  connect_failed?: '1';
  // Set by the ⌘K palette: the integration card to scroll to
  // (useFocusIntegration).
  focus?: string;
}

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v ? v : undefined;

// Card ids are lower-case words, so the value is always a safe selector.
const integrationId = (v: unknown): string | undefined =>
  typeof v === 'string' && /^[a-z_]+$/.test(v) ? v : undefined;

const integrationsTab = (value: unknown): IntegrationsSearch['tab'] =>
  value === 'integrations' ||
  value === 'extension' ||
  value === 'mcp' ||
  value === 'email-accounts'
    ? value
    : undefined;

export const Route = createFileRoute('/integrations/')({
  // Sender connections moved to Sequencing settings; the old tab and the
  // builder's deep links land there with their params.
  beforeLoad: ({ search }) => {
    if (search.tab === 'email-accounts' || search.connect === 'email')
      throw redirect({
        to: '/sequencing/settings',
        search: {
          section: 'senders',
          connect: search.connect,
          connected: search.connected,
          connect_failed: search.connect_failed,
        },
      });
  },
  validateSearch: (search: Record<string, unknown>): IntegrationsSearch => ({
    hubspot_connected: str(search.hubspot_connected),
    hubspot_error: str(search.hubspot_error),
    sf_connected: str(search.sf_connected),
    sf_error: str(search.sf_error),
    pipedrive_connected: str(search.pipedrive_connected),
    pipedrive_error: str(search.pipedrive_error),
    attio_connected: str(search.attio_connected),
    attio_error: str(search.attio_error),
    lemlist_error: str(search.lemlist_error),
    outreach: str(search.outreach),
    google_connected: str(search.google_connected),
    google_error: str(search.google_error),
    fireflies_connected: str(search.fireflies_connected),
    fireflies_error: str(search.fireflies_error),
    fathom_connected: str(search.fathom_connected),
    fathom_error: str(search.fathom_error),
    gong_connected: str(search.gong_connected),
    gong_error: str(search.gong_error),
    notion_connected: str(search.notion_connected),
    notion_error: str(search.notion_error),
    airtable_connected: str(search.airtable_connected),
    airtable_error: str(search.airtable_error),
    sixsense_mcp_connected: str(search.sixsense_mcp_connected),
    sixsense_mcp_error: str(search.sixsense_mcp_error),
    importCrm: str(search.importCrm),
    tab: integrationsTab(search.tab),
    connect: search.connect === 'email' ? 'email' : undefined,
    connected: search.connected !== undefined ? '1' : undefined,
    connect_failed: search.connect_failed !== undefined ? '1' : undefined,
    focus: integrationId(search.focus),
  }),
  component: RouteComponent,
});

/** Toast the OAuth connect outcome once, then strip the params so a refresh or
 * back-navigation never re-fires the toast. */
function useOAuthOutcomeToast() {
  const {
    hubspot_connected,
    hubspot_error,
    sf_connected,
    sf_error,
    pipedrive_connected,
    pipedrive_error,
    attio_connected,
    attio_error,
    lemlist_error,
    outreach,
    google_connected,
    google_error,
    fireflies_connected,
    fireflies_error,
    fathom_connected,
    fathom_error,
    gong_connected,
    gong_error,
    notion_connected,
    notion_error,
    airtable_connected,
    airtable_error,
    sixsense_mcp_connected,
    sixsense_mcp_error,
  } = Route.useSearch();
  const navigate = useNavigate();

  useEffect(() => {
    const hasOutcome =
      hubspot_connected ||
      hubspot_error ||
      sf_connected ||
      sf_error ||
      pipedrive_connected ||
      pipedrive_error ||
      attio_connected ||
      attio_error ||
      lemlist_error ||
      outreach ||
      google_connected ||
      google_error ||
      fireflies_connected ||
      fireflies_error ||
      fathom_connected ||
      fathom_error ||
      gong_connected ||
      gong_error ||
      notion_connected ||
      notion_error ||
      airtable_connected ||
      airtable_error ||
      sixsense_mcp_connected ||
      sixsense_mcp_error;
    if (!hasOutcome) return;

    // A window Clu's connection card opened tells the card how it went and
    // closes; the toast below is for a window the browser keeps open.
    const failure =
      hubspot_error ??
      sf_error ??
      pipedrive_error ??
      attio_error ??
      lemlist_error ??
      (outreach && outreach !== 'connected'
        ? outreach === 'expired'
          ? 'expired_retry'
          : outreach
        : undefined) ??
      google_error ??
      fireflies_error ??
      fathom_error ??
      gong_error ??
      notion_error ??
      airtable_error ??
      sixsense_mcp_error;
    finishConnectPopup(!failure, failure);

    if (hubspot_connected) toast.success('HubSpot connected.');
    if (sf_connected) toast.success('Salesforce connected.');
    if (google_connected)
      toast.success(
        `${google_connected === 'google_docs' ? 'Google Docs' : google_connected === 'google_calendar' ? 'Google Calendar' : 'Google Sheets'} connected.`
      );
    if (hubspot_error)
      toast.error(
        `HubSpot connection failed: ${humanizeOAuthError(hubspot_error)}`
      );
    if (sf_error)
      toast.error(
        `Salesforce connection failed: ${humanizeOAuthError(sf_error)}`
      );
    if (pipedrive_connected) toast.success('Pipedrive connected.');
    if (pipedrive_error)
      toast.error(
        `Pipedrive connection failed: ${humanizeOAuthError(pipedrive_error)}`
      );
    if (attio_connected) toast.success('Attio connected.');
    if (attio_error)
      toast.error(
        `Attio connection failed: ${ATTIO_ERROR_LABELS[attio_error] ?? humanizeOAuthError(attio_error)}`
      );
    if (lemlist_error)
      toast.error(
        `Lemlist connection failed: ${humanizeOAuthError(lemlist_error)}`
      );
    if (outreach === 'connected') toast.success('Outreach connected.');
    else if (outreach)
      toast.error(
        `Outreach connection failed: ${humanizeOAuthError(outreach === 'expired' ? 'expired_retry' : outreach)}`
      );
    if (google_error)
      toast.error(
        `Google connection failed: ${humanizeOAuthError(google_error)}`
      );
    if (fireflies_connected)
      toast.success(
        'Fireflies connected. Your team’s meetings are syncing into Knowledge.'
      );
    if (fireflies_error)
      toast.error(
        `Fireflies connection failed: ${humanizeOAuthError(fireflies_error)}`
      );
    if (fathom_connected)
      toast.success(
        'Fathom connected. Your meetings are syncing into Knowledge.'
      );
    if (fathom_error)
      toast.error(
        `Fathom connection failed: ${humanizeOAuthError(fathom_error)}`
      );
    if (gong_connected)
      toast.success(
        'Gong connected. Your team’s calls are syncing into Knowledge.'
      );
    if (gong_error)
      toast.error(`Gong connection failed: ${humanizeOAuthError(gong_error)}`);
    if (notion_connected)
      toast.success(
        'Notion connected. The pages you shared are syncing into Knowledge.'
      );
    if (notion_error)
      toast.error(
        `Notion connection failed: ${humanizeOAuthError(notion_error)}`
      );
    if (airtable_connected)
      toast.success('Airtable connected. Clu can now export lists to it.');
    if (airtable_error)
      toast.error(
        `Airtable connection failed: ${humanizeOAuthError(airtable_error)}`
      );
    if (sixsense_mcp_connected)
      toast.success('6sense connected. Clu can now read your 6sense.');
    if (sixsense_mcp_error)
      toast.error(
        `6sense connection failed: ${humanizeOAuthError(sixsense_mcp_error)}`
      );

    navigate({
      to: '/integrations',
      search: {},
      replace: true,
    });
  }, [
    hubspot_connected,
    hubspot_error,
    sf_connected,
    sf_error,
    pipedrive_connected,
    pipedrive_error,
    attio_connected,
    attio_error,
    lemlist_error,
    outreach,
    google_connected,
    google_error,
    fireflies_connected,
    fireflies_error,
    fathom_connected,
    fathom_error,
    gong_connected,
    gong_error,
    notion_connected,
    notion_error,
    airtable_connected,
    airtable_error,
    sixsense_mcp_connected,
    sixsense_mcp_error,
    navigate,
  ]);
}

/** Scroll to the card `focus` names, then drop the param so picking the same
 * integration again scrolls again. */
function useFocusIntegration() {
  const { focus } = Route.useSearch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!focus) return;
    return focusIntegrationCard(focus, () => {
      void navigate({
        to: '/integrations',
        search: {},
        replace: true,
      });
    });
  }, [focus, navigate]);
}

function RouteComponent() {
  useOAuthOutcomeToast();
  useFocusIntegration();
  const { tab } = Route.useSearch();
  return (
    <>
      <Head heading='Integrations' />
      <AuthCheck>
        <AppLayout variant='full-bleed'>
          <IntegrationsGate initialTab={tab} />
        </AppLayout>
      </AuthCheck>
    </>
  );
}
